import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ClipboardList, ListChecks } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { ModuleSelector } from "@/components/ModuleSelector";
import { ComplianceOverview } from "@/components/ComplianceOverview";
import { FadeUp, MaskLines } from "@/components/landing/Reveal";
import { api, RISK } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AuditRow = ({ a, i }) => {
  const r = RISK[a.risk] ?? RISK.Alto;
  return (
    <FadeUp delay={i * 0.04}>
      <Link to={`/resumen/${a.id}`} data-testid={`audit-row-${a.id}`} className="group grid grid-cols-12 gap-4 items-center border-b border-white/10 py-6 hover:bg-white/[0.02] transition-colors px-2">
        <div className="col-span-12 sm:col-span-5">
          <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#F59E0B]" data-testid={`audit-row-module-${a.id}`}>{a.module_name}{a.pillar === "iso" ? " · Calidad" : ""}</div>
          <div className="mt-1 font-display text-xl font-bold group-hover:text-[#F59E0B] transition-colors">{a.company}</div>
          <div className="text-sm text-[#F5F1E8]/65">{a.site}</div>
        </div>
        <div className="col-span-4 sm:col-span-2 font-mono text-xs text-[#F5F1E8]/65">{new Date(a.created_at).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" })}{a.no > 0 && <span className="block mt-1 text-emerald-400/90" data-testid={`audit-row-resolved-${a.id}`}>{a.resolved ?? 0}/{a.no} resueltos</span>}</div>
        <div className="col-span-3 sm:col-span-2 font-display-x text-2xl font-black">{a.score}%</div>
        <div className="col-span-5 sm:col-span-3 flex items-center justify-end gap-3">
          <span className={`px-3 py-1 rounded-full border text-xs font-semibold ${r.bg} ${r.border} ${r.text}`}>{r.label}</span>
          <ArrowUpRight className="w-4 h-4 text-[#F5F1E8]/50 group-hover:rotate-45 group-hover:text-[#F59E0B] transition-transform" />
        </div>
      </Link>
    </FadeUp>
  );
};

const FixProgress = ({ audits }) => {
  const total = audits.reduce((n, a) => n + (a.no ?? 0), 0);
  const done = audits.reduce((n, a) => n + (a.resolved ?? 0), 0);
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <FadeUp className="mt-14 border border-white/10 bg-[#111722] p-6 sm:p-8" data-testid="fix-progress-card">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#F5F1E8]/60">Hallazgos solucionados</div>
          <div className="mt-2 font-display-x text-5xl font-black" data-testid="fix-progress-value">{done}<span className="text-[#F5F1E8]/40 text-3xl"> / {total}</span></div>
        </div>
        <div className="font-display-x text-3xl font-black text-emerald-400" data-testid="fix-progress-pct">{pct}%</div>
      </div>
      <div className="mt-5 h-2.5 bg-white/5 overflow-hidden">
        <motion.div className="h-full bg-emerald-400" initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} data-testid="fix-progress-bar" />
      </div>
      <p className="mt-4 text-sm text-[#F5F1E8]/65">{total === 0 ? "Sin hallazgos pendientes en tus auditorías." : "Todos los “No” de todas tus auditorías, en un solo plan de acción ordenado por prioridad."}</p>
      {total > 0 && (
        <Link to="/plan-de-accion" data-testid="fix-progress-plan-link" className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#F59E0B] hover:text-[#F5F1E8] transition-colors">
          Abrir plan de acción centralizado <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" />
        </Link>
      )}
    </FadeUp>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const [audits, setAudits] = useState(null);

  useEffect(() => {
    api.get("/audits").then((r) => setAudits(Array.isArray(r.data) ? r.data : [])).catch(() => setAudits([]));
  }, []);

  return (
    <main className="min-h-screen" data-testid="dashboard-page">
      <Navbar variant="app" />
      <div className="mx-auto max-w-[1200px] px-5 sm:px-8 pt-32 pb-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div>
            <div className="font-mono text-xs uppercase tracking-[0.22em] text-[#F59E0B]">Panel · {user?.name}</div>
            <h1 className="font-display-x mt-5 text-5xl sm:text-7xl font-black uppercase leading-[0.9] tracking-tight">
              <MaskLines lines={["Mis", "auditorías"]} />
            </h1>
          </div>
          <Link to="/plan-de-accion" data-testid="nav-action-center-button" className="group inline-flex w-fit items-center gap-3 bg-[#F59E0B] text-[#0B0F17] pl-6 pr-2 py-2 rounded-full font-semibold hover:bg-[#F5F1E8] transition-colors">
            Plan de acción
            <span className="grid place-items-center w-10 h-10 rounded-full bg-[#0B0F17] text-[#F59E0B] transition-transform group-hover:rotate-12"><ListChecks className="w-4 h-4" /></span>
          </Link>
        </div>

        {audits?.length > 0 && <ComplianceOverview audits={audits} />}

        <ModuleSelector />

        {audits?.length > 0 && <FixProgress audits={audits} />}

        <h2 className="font-display mt-16 text-2xl sm:text-3xl font-bold">Historial de resultados</h2>
        <div className="mt-6 border-t border-white/10" data-testid="audit-list">
          {audits === null && <div className="py-16 font-mono text-sm text-[#F5F1E8]/60">Cargando…</div>}
          {audits?.length === 0 && (
            <FadeUp className="py-20 flex flex-col items-start gap-5" data-testid="audit-empty-state">
              <ClipboardList className="w-10 h-10 text-[#F59E0B]" strokeWidth={1.3} />
              <p className="font-display text-2xl font-bold">Aún no tienes auditorías.</p>
              <p className="text-[#F5F1E8]/70 max-w-md">Elige un módulo del catálogo y obtén tu porcentaje de cumplimiento y nivel de riesgo en minutos.</p>
            </FadeUp>
          )}
          {audits?.map((a, i) => <AuditRow key={a.id} a={a} i={i} />)}
        </div>
      </div>
    </main>
  );
}
